import React, { useCallback, useContext } from 'react'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import TextLink from 'components/Luxkit/TextLink'
import Caption from 'components/Luxkit/Typography/Caption'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import { OfferPackageOptionView, getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import { useAppDispatch } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import BnblModal from './BnblModal/BnblModal'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import LineGiftIcon from 'components/Luxkit/Icons/line/LineGiftIcon'
import getDatasetFromEvent from 'lib/analytics/getDatasetFromEvent'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import Group from 'components/utils/Group'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getBnblPackageViewByNumberOfRooms } from './BnblModal/utils'

interface Props {
  offer: App.HotelOffer;
  baseBnblPackageViews: Array<OfferPackageOptionView>;
  packageView: OfferPackageOptionView;
  rooms: Array<App.Occupants>;
  direction?: 'vertical' | 'horizontal';
}

function OfferPageBNBLcta(props: Props) {
  const dispatch = useAppDispatch()
  const intlDateFormatter = useIntlDateFormatter()
  const [bnblModalOpen, _, openBnblModal, closeBnblModal] = useToggle()
  const { offer, baseBnblPackageViews, packageView, rooms, direction = 'vertical' } = props

  const {
    package: pkg,
  } = packageView

  const isSevenDaysBeforeTravelToDate = moment().add(1, 'w').isBefore(offer.travelToDate)
  const isMemberOnlyOffer = offer.luxPlus.access === 'memberOnly'

  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const openAndInitaliseBnblModal = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    trackEvent('BUY_NOW_CHOOSE_DATES_LATER_PRESSED')
    e.preventDefault()
    openBnblModal()
    const isGift = getDatasetFromEvent(e, 'bnbltype') === 'gift'
    const numberOfRooms = rooms?.length ?? 1
    const bnblPackageView = baseBnblPackageViews.find(baseBnblPackageView => baseBnblPackageView.package.uniqueKey === pkg.uniqueKey)
    if (!bnblPackageView) return

    const packages = [{ packageView: getBnblPackageViewByNumberOfRooms(bnblPackageView, numberOfRooms), numberOfRooms }]
    dispatch(initialiseBnblOfferModal(packages, offer.id, isGift))
  }, [baseBnblPackageViews, dispatch, offer.id, openBnblModal, pkg.uniqueKey, rooms?.length, trackEvent])

  return (<>
    <Group direction={direction} gap={direction === 'horizontal' ? 16 : 4}>
      <TextLink
        size="caption"
        onClick={openAndInitaliseBnblModal}
        data-testid="buyNowChooseDatesLater"
        startIcon={<LineCalendarIcon />}
      >
        Buy now, choose dates later
      </TextLink>

      {isSevenDaysBeforeTravelToDate && !isMemberOnlyOffer && <TextLink
        data-bookingtype="gift"
        size="caption"
        onClick={openAndInitaliseBnblModal}
        data-testid="sendAsGift"
        data-bnbltype="gift"
        startIcon={<LineGiftIcon />}
      >
        Give as gift
      </TextLink>}
      {!isSevenDaysBeforeTravelToDate && offer.travelToDate && <Caption variant="medium">
        Travel until {intlDateFormatter(offer.travelToDate, INTL_DMY_CASUAL_FORMAT)}
      </Caption>}
    </Group>
    {bnblModalOpen && <BnblModal closeBnblModal={closeBnblModal} isOpen={bnblModalOpen} offer={offer}/>}
  </>)
}

export default withOfferPageState((state) => ({
  rooms: state.rooms,
  offer: state.offer,
  baseBnblPackageViews: getBnblPackageViews(state),
}), OfferPageBNBLcta)
